import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Button,
  Grid,
  Typography,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import axios from "axios";
import Layout from "../../layout/Layout";
import { useNavigate } from "react-router-dom";
import {
  config,
  manageCompaniesAPI,
  poDateAPI,
  purchaseOrderAPI,
} from "../../config";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import MuiLink from "@mui/material/Link";
import { LoadingButton } from "@mui/lab";
import DataTable from "react-data-table-component";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import moment from "moment";
import * as XLSX from "xlsx/xlsx.mjs";
import DescriptionIcon from "@mui/icons-material/Description";

function PoWorkOrderReport() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [reportList, setReportList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const getFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const startYear = month >= 4 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };

  const [orderType, setOrderType] = useState(0);
  const [poDate, setPoDate] = useState(getFinancialYear());
  const [poDateList, setPoDateList] = useState([]);
  const [companyName, setCompanyName] = useState(0);
  const [companyNames, setCompanyNames] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [excellData, setExcellData] = useState([]);

  const poTypeList = [
    { label: "All", value: 0 },
    { label: "Purchase Order", value: 1 },
    { label: "Work Order", value: 2 },
  ];

  // Fetch Financial Years
  useEffect(() => {
    const configFetch = {
      method: "POST",
      url: poDateAPI,
      headers: {
        "Content-Type": "application/json",
      },
    };
    axios(configFetch)
      .then((response) => {
        const { minYear, maxYear } = response.data.result;
        const financialYears = [];
        for (let year = minYear; year <= maxYear; year++) {
          const nextYear = year + 1;
          const label = `${year}-${nextYear}`;
          financialYears.push({ value: label, label });
        }
        setPoDateList(financialYears);
      })
      .catch((error) => {
        console.error("Error fetching PO date list:", error);
      });
  }, []);

  // Fetch Companies
  useEffect(() => {
    const configComp = {
      method: "POST",
      url: manageCompaniesAPI,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify({ intType: 5 }),
    };
    axios(configComp)
      .then((response) => {
        const allOption = { id: 0, company_name: "All" };
        setCompanyNames([allOption, ...response.data.result]);
      })
      .catch((error) => {
        console.error("Error fetching companies:", error);
      });
  }, []);

  // Fetch report data
  const onSearch = async (page = 1, limit = rowsPerPage) => {
    setLoading(true);
    const data = JSON.stringify({
      txtOrderType: orderType,
      txtPODate: poDate,
      txtCompanyName: companyName,
      txtSearchString: searchString,
      txtSortDirection: "desc",
      intLimit: limit,
      intOffset: (page - 1) * limit,
      intType: 23,
    });

    const configReq = {
      ...config,
      method: "POST",
      data,
      url: purchaseOrderAPI,
    };

    try {
      const response = await axios(configReq);
      setReportList(response.data.data || []);
      setTotalCount(response.data.total_count || 0);

      // Fetch all matching data for Excel export
      const excelConfig = {
        ...configReq,
        data: JSON.stringify({
          txtOrderType: orderType,
          txtPODate: poDate,
          txtCompanyName: companyName,
          txtSearchString: searchString,
          txtSortDirection: "desc",
          intLimit: 100000,
          intOffset: 0,
          intType: 23,
        }),
      };
      const excelRes = await axios(excelConfig);
      setExcellData(excelRes.data.data || []);
    } catch (error) {
      console.error("Error fetching PO/WO report data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onSearch(1, rowsPerPage);
    setCurrentPage(1);
  }, [orderType, poDate, companyName]);

  const handlePageChange = (page) => {
    onSearch(page, rowsPerPage);
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = async (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    onSearch(1, newRowsPerPage);
    setCurrentPage(1);
  };

  const findType = (invoiceType) => {
    if (Number(invoiceType) === 1) return "Purchase Order";
    if (Number(invoiceType) === 2) return "Work Order";
    return "-";
  };

  const formatAmount = (val) => {
    if (!val) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(val);
  };

  const columns = useMemo(() => [
    {
      name: "PO/WO No",
      selector: (row) => row.po_no || "-",
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => row.po_date ? moment(row.po_date).format("DD-MM-YYYY") : "-",
      sortable: true,
    },
    {
      name: "Order Type",
      selector: (row) => findType(row.order_type),
      sortable: true,
    },
    {
      name: "Vendor Name",
      selector: (row) => row.vendor_name || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Company Name",
      selector: (row) => row.company_Name || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Project Name",
      selector: (row) => row.project_name || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Amount (Rs.)",
      selector: (row) => formatAmount(row.total_with_gst),
      sortable: true,
      right: true,
    },
    {
      name: "Status",
      selector: (row) => row.status === 100 ? "Cancelled" : "Active",
      sortable: true,
    },
  ], []);

  const downloadExcel = () => {
    try {
      const dataToExport = excellData.map((row) => ({
        "PO/WO Number": row.po_no || "",
        "Date": row.po_date ? moment(row.po_date).format("DD-MM-YYYY") : "",
        "Order Type": findType(row.order_type),
        "Vendor Name": row.vendor_name || "",
        "Company Name": row.company_Name || "",
        "Project Name": row.project_name || "",
        "Amount": row.total_with_gst || 0,
        "Status": row.status === 100 ? "Cancelled" : "Active",
      }));

      const workSheet = XLSX.utils.json_to_sheet(dataToExport);
      const workBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workBook, workSheet, "PO-WO-Report");
      XLSX.writeFile(
        workBook,
        `PO_WO_Report_${moment().format("DD_MM_YYYY_HH_mm_ss")}.xlsx`
      );
    } catch (error) {
      console.error("Excel download failed:", error);
    }
  };

  const customStyles = {
    headCells: {
      style: {
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
  };

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid item xs={12} lg={12} md={12}>
          <Grid container spacing={3}>
            <Grid textAlign="center" item className="form-lable" xs={12} lg={6} md={6}>
              <Typography className="page_title" textAlign="left">
                PO & Work Order Report
              </Typography>
              <Breadcrumbs aria-label="breadcrumb">
                <MuiLink underline="hover" color="inherit" href="/dashboard">
                  Dashboard
                </MuiLink>
                <Typography color="text.primary">Reports</Typography>
              </Breadcrumbs>
            </Grid>
            <Grid textAlign="right" item className="form-lable" xs={12} lg={6} md={6}>
              <LoadingButton
                size="medium"
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
              >
                Back
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
        <div className="empty-space-40"></div>

        <Grid
          container
          spacing={2}
          margin={`15px 0px`}
          padding={`10px 15px 0px 0px`}
          alignItems={`center`}
        >
          <Grid item xs={12} lg={2} md={3}>
            <FormControl fullWidth>
              <InputLabel id="order-type-label">Order Type</InputLabel>
              <Select
                labelId="order-type-label"
                value={orderType}
                label="Order Type"
                onChange={(e) => setOrderType(e.target.value)}
              >
                {poTypeList.map((row, index) => (
                  <MenuItem value={row.value} key={index}>
                    {row.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} lg={2} md={3}>
            <FormControl fullWidth>
              <InputLabel id="financial-year-label">Financial Year</InputLabel>
              <Select
                labelId="financial-year-label"
                value={poDate}
                label="Financial Year"
                onChange={(e) => setPoDate(e.target.value)}
              >
                {poDateList.map((row, index) => (
                  <MenuItem value={row.value} key={index}>
                    {row.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} lg={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="company-name-label">Company Name</InputLabel>
              <Select
                labelId="company-name-label"
                value={companyName}
                label="Company Name"
                onChange={(e) => setCompanyName(e.target.value)}
              >
                {companyNames.map((row, index) => (
                  <MenuItem value={row.id} key={index}>
                    {row.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} lg={4} md={3}>
            <TextField
              label="Search"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              variant="outlined"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
            <LoadingButton
              size="large"
              variant="contained"
              color="primary"
              onClick={() => onSearch(1, rowsPerPage)}
              sx={{ height: "48px", minWidth: "150px" }}
            >
              Search
            </LoadingButton>
            <Button
              color="success"
              startIcon={<DescriptionIcon />}
              onClick={downloadExcel}
              variant="contained"
              sx={{ height: "48px", minWidth: "180px" }}
            >
              Export to Excel
            </Button>
          </Grid>
        </Grid>

        <Grid className="form_design">
          <Grid>
            <DataTable
              pagination
              paginationServer
              fixedHeader
              fixedHeaderScrollHeight="550px"
              columns={columns}
              data={reportList}
              progressPending={loading}
              paginationTotalRows={totalCount}
              paginationDefaultPage={currentPage}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              paginationPerPage={rowsPerPage}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
              customStyles={customStyles}
            />
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default PoWorkOrderReport;
